import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useTenant } from "@/context/TenantContext";
import { PageHeader, Card, Button, Input, Textarea, Label } from "@/components/Primitives";
import { Sun, Moon, Palette, Check, Zap, RefreshCw, Copy, Database, Smartphone } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  { name: "Amber (Beacon)",    h: 36,  s: 95,  l: 52 },
  { name: "Cobalt (SAL)",      h: 222, s: 100, l: 50 },
  { name: "Indigo (Soubor)",   h: 231, s: 75,  l: 50 },
  { name: "Emerald",           h: 160, s: 84,  l: 39 },
  { name: "Rose",              h: 346, s: 77,  l: 49 },
  { name: "Violet",            h: 262, s: 83,  l: 58 },
  { name: "Slate",             h: 215, s: 16,  l: 34 },
];

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const isAdmin = user?.role === "admin" || user?.role === "team";

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader breadcrumb="Account · Settings" title="Settings" subtitle="Profile, preferences, branding, and integrations." />

      <Card title="Profile">
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Name</Label><Input defaultValue={user?.name || ""} /></div>
          <div><Label>Email</Label><Input defaultValue={user?.email || ""} disabled /></div>
          <div><Label>Role</Label><Input defaultValue={user?.role || ""} disabled className="font-mono"/></div>
          {user?.customer_id && <div><Label>Customer ID</Label><Input defaultValue={user?.customer_id || ""} disabled className="font-mono"/></div>}
        </div>
      </Card>

      <Card title="Appearance">
        <div className="p-5 flex items-center gap-3">
          {[{ id: "light", l: "Light", i: Sun }, { id: "dark", l: "Dark", i: Moon }].map((t) => (
            <button key={t.id} onClick={() => setTheme(t.id)} className={`inline-flex items-center gap-2 px-3 h-10 rounded-lg border transition-all ${theme === t.id ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]" : "border-[hsl(var(--border))] text-[hsl(var(--text-muted))]"}`}>
              <t.i className="w-4 h-4" strokeWidth={1.6}/>{t.l}
            </button>
          ))}
        </div>
      </Card>

      {isAdmin && <BrandingCard />}
      {isAdmin && <FirestoreCard />}
      {isAdmin && <StripeCard />}
      {isAdmin && <GoodBarberCard />}

      <Card title="Integrations">
        <div className="p-5 space-y-3 text-[13px]">
          {[
            { name: "Firebase Auth", desc: "Live. Admin emails auto-promote; Google + Email/Password enabled.", status: "Connected", variant: "success" },
            { name: "Stripe", desc: "Live account connected. Backfill + webhook sync below.", status: "Connected", variant: "success" },
            { name: "GoodBarber API", desc: "App stats, versions, push volumes.", status: "Connect later", variant: "secondary" },
          ].map((i) => (
            <div key={i.name} className="flex items-center justify-between p-3 rounded-lg border border-[hsl(var(--border))]">
              <div>
                <div className="font-semibold flex items-center gap-2">
                  {i.name}
                  {i.variant === "success" && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[hsl(var(--success-muted))] text-[hsl(var(--success))]"><span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--success))]"/>Live</span>}
                </div>
                <div className="text-[12px] text-[hsl(var(--text-muted))]">{i.desc}</div>
              </div>
              <Button variant={i.variant === "success" ? "ghost" : "secondary"} size="sm">{i.status}</Button>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Danger zone">
        <div className="p-5">
          <Button variant="danger" onClick={logout} data-testid="settings-logout">Log out</Button>
        </div>
      </Card>
    </div>
  );
}

function BrandingCard() {
  const { tenant, update, refresh } = useTenant();
  const [form, setForm] = useState(tenant);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(tenant); }, [tenant]);
  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyPreset = (p) => setForm((f) => ({ ...f, primary_hue: p.h, primary_saturation: p.s, primary_lightness: p.l }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await update({
        name: form.name,
        tagline: form.tagline,
        subtitle: form.subtitle,
        logo_mark: form.logo_mark,
        logo_url: form.logo_url || null,
        primary_hue: Number(form.primary_hue),
        primary_saturation: Number(form.primary_saturation),
        primary_lightness: Number(form.primary_lightness),
        powered_by: form.powered_by || null,
        support_email: form.support_email || null,
        footer_text: form.footer_text || null,
      });
      toast.success(`Brand updated. Everyone now sees "${form.name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <Card title="Branding (whitelabel)" right={<span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))] font-semibold"><Palette className="w-3 h-3" strokeWidth={2}/>Owner only</span>}>
      <form onSubmit={save} className="p-5 space-y-5">
        <div className="text-[12px] text-[hsl(var(--text-muted))] leading-relaxed bg-[hsl(var(--primary-muted))] border border-[hsl(var(--primary)/0.15)] rounded-lg p-3">
          <strong className="text-[hsl(var(--primary))]">Heads up:</strong> changes apply <em>instantly</em> for everyone using this tenant.
          This is the scaffolding for whitelabel. Later you can create separate tenants per customer/agency.
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Brand name</Label><Input value={form.name || ""} onChange={(e) => s("name", e.target.value)} data-testid="brand-name"/></div>
          <div>
            <Label>"Powered by" footer</Label>
            <Input value={form.powered_by || ""} onChange={(e) => s("powered_by", e.target.value)} placeholder="e.g. SAL (leave blank to hide)"/>
          </div>
          <div><Label>Logo letter / mark</Label><Input maxLength={2} value={form.logo_mark || ""} onChange={(e) => s("logo_mark", e.target.value)} className="font-mono"/></div>
          <div><Label>Logo image URL (optional)</Label><Input value={form.logo_url || ""} onChange={(e) => s("logo_url", e.target.value)} placeholder="https://…"/></div>
          <div className="md:col-span-2"><Label>Tagline (big headline)</Label><Input value={form.tagline || ""} onChange={(e) => s("tagline", e.target.value)}/></div>
          <div className="md:col-span-2"><Label>Subtitle (under tagline)</Label><Textarea value={form.subtitle || ""} onChange={(e) => s("subtitle", e.target.value)} rows={2}/></div>
          <div><Label>Support email</Label><Input type="email" value={form.support_email || ""} onChange={(e) => s("support_email", e.target.value)} placeholder="help@soubor.one"/></div>
          <div><Label>Footer text</Label><Input value={form.footer_text || ""} onChange={(e) => s("footer_text", e.target.value)} placeholder="© 2026 Soubor One"/></div>
        </div>

        {/* Accent presets */}
        <div>
          <Label>Accent colour</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {PRESETS.map((p) => {
              const active = Number(form.primary_hue) === p.h;
              return (
                <button type="button" key={p.name} onClick={() => applyPreset(p)}
                  className={`inline-flex items-center gap-2 px-3 h-9 rounded-lg border text-[12px] font-semibold transition-all ${active ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]" : "border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:border-[hsl(var(--border-strong))]"}`}>
                  <span className="w-3 h-3 rounded-full" style={{ background: `hsl(${p.h} ${p.s}% ${p.l}%)` }}/>
                  {p.name}
                  {active && <Check className="w-3 h-3" strokeWidth={2}/>}
                </button>
              );
            })}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <div>
              <Label>Hue</Label>
              <input type="range" min="0" max="360" value={form.primary_hue} onChange={(e) => s("primary_hue", e.target.value)} className="w-full accent-[hsl(var(--primary))] mt-2"/>
              <div className="text-[11px] font-mono text-[hsl(var(--text-muted))]">{form.primary_hue}°</div>
            </div>
            <div>
              <Label>Saturation</Label>
              <input type="range" min="0" max="100" value={form.primary_saturation} onChange={(e) => s("primary_saturation", e.target.value)} className="w-full accent-[hsl(var(--primary))] mt-2"/>
              <div className="text-[11px] font-mono text-[hsl(var(--text-muted))]">{form.primary_saturation}%</div>
            </div>
            <div>
              <Label>Lightness</Label>
              <input type="range" min="20" max="80" value={form.primary_lightness} onChange={(e) => s("primary_lightness", e.target.value)} className="w-full accent-[hsl(var(--primary))] mt-2"/>
              <div className="text-[11px] font-mono text-[hsl(var(--text-muted))]">{form.primary_lightness}%</div>
            </div>
          </div>
        </div>

        {/* Preview pill */}
        <div className="p-4 rounded-lg border border-dashed border-[hsl(var(--border))] flex items-center gap-3">
          <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Live preview</div>
          <div className="inline-flex items-center gap-2 px-3 h-9 rounded-lg font-semibold text-white text-[13px]"
               style={{ background: `hsl(${form.primary_hue} ${form.primary_saturation}% ${form.primary_lightness}%)` }}>
            {form.name || "Brand"} button
          </div>
          <div className="text-[12px] text-[hsl(var(--text-muted))]">{form.powered_by ? `Powered by ${form.powered_by}` : "No powered-by footer"}</div>
        </div>

        <div className="flex gap-2">
          <Button type="submit" disabled={saving} data-testid="save-branding">{saving ? "Saving…" : <><Check className="w-4 h-4" strokeWidth={2}/>Save branding</>}</Button>
          <Button type="button" variant="secondary" onClick={() => refresh()}>Reset</Button>
        </div>
      </form>
    </Card>
  );
}

function StripeCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stripe/status");
      setStatus(data);
    } catch (e) {
      setStatus({ connected: false, error: e?.response?.data?.detail || "Unknown" });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/stripe/sync");
      toast.success(`Synced ${data.customers_synced} customers · ${data.invoices_synced} invoices`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const copyUrl = () => {
    if (!status?.webhook_url) return;
    navigator.clipboard?.writeText(status.webhook_url);
    toast.success("Webhook URL copied");
  };

  if (loading) return <Card title="Stripe"><div className="p-5 text-[13px] text-[hsl(var(--text-muted))]">Loading…</div></Card>;

  return (
    <Card title="Stripe integration" right={
      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-semibold ${status?.connected ? "bg-[hsl(var(--success-muted))] text-[hsl(var(--success))]" : "bg-[hsl(var(--danger-muted))] text-[hsl(var(--danger))]"}`}>
        <Zap className="w-3 h-3" strokeWidth={2}/>{status?.connected ? `Live · ${status?.mode || "live"}` : "Not connected"}
      </span>
    }>
      <div className="p-5 space-y-4">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Customers linked" value={status?.customers_linked ?? 0} />
          <MiniStat label="Invoices synced" value={status?.invoices_linked ?? 0} />
          <MiniStat label="Webhook events" value={status?.events_processed_total ?? 0} />
          <MiniStat label="Last sync" value={status?.last_sync_at ? new Date(status.last_sync_at).toLocaleString() : "Never"} tight />
        </div>

        {/* Webhook setup */}
        <div className="rounded-lg border border-[hsl(var(--border))] p-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div>
              <div className="text-[13px] font-semibold">Webhook endpoint</div>
              <div className="text-[11px] text-[hsl(var(--text-muted))]">Add this in Stripe Dashboard → Developers → Webhooks</div>
            </div>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-semibold ${status?.webhook_secret_configured ? "bg-[hsl(var(--success-muted))] text-[hsl(var(--success))]" : "bg-[hsl(var(--warning-muted))] text-[hsl(var(--warning))]"}`}>
              {status?.webhook_secret_configured ? "Secret set" : "Secret missing"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-[12px] bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded-lg px-3 py-2 truncate">{status?.webhook_url}</code>
            <Button variant="secondary" size="sm" onClick={copyUrl}><Copy className="w-3.5 h-3.5" strokeWidth={1.6}/>Copy</Button>
          </div>
          {status?.webhook_last_received_at && (
            <div className="mt-2 text-[11px] text-[hsl(var(--text-muted))]">
              Last event: <span className="font-mono">{status.webhook_last_type}</span> · {new Date(status.webhook_last_received_at).toLocaleString()}
            </div>
          )}
          {!status?.webhook_secret_configured && (
            <div className="mt-3 text-[12px] text-[hsl(var(--text-muted))] leading-relaxed bg-[hsl(var(--warning-muted))]/50 border border-[hsl(var(--warning))]/20 rounded-lg p-3">
              <strong className="text-[hsl(var(--warning))]">Action needed:</strong> create the webhook endpoint in Stripe, then paste the <code className="font-mono">whsec_…</code> signing secret into <code>backend/.env</code> as <code>STRIPE_WEBHOOK_SECRET</code> and restart the backend.
            </div>
          )}
        </div>

        {/* Sync controls */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[12px] text-[hsl(var(--text-muted))] leading-relaxed">
            Backfill customers + last 200 invoices from Stripe. Safe to run anytime. Upserts by Stripe IDs.
          </div>
          <Button onClick={doSync} disabled={syncing} data-testid="stripe-sync-btn">
            {syncing ? <><RefreshCw className="w-4 h-4 animate-spin" strokeWidth={2}/>Syncing…</> : <><RefreshCw className="w-4 h-4" strokeWidth={2}/>Sync from Stripe</>}
          </Button>
        </div>
      </div>
    </Card>
  );
}


function FirestoreCard() {
  const [s, setS] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const load = async () => {
    try { setS((await api.get("/admin/firestore/status")).data); }
    catch { setS({ connected: false }); }
  };
  useEffect(() => { load(); }, []);
  const doSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/firestore/sync");
      const upserted = Object.entries(data).filter(([k]) => k.endsWith("_synced")).reduce((s, [, v]) => s + (v || 0), 0);
      const deleted = Object.entries(data).filter(([k]) => k.endsWith("_deleted")).reduce((s, [, v]) => s + (v || 0), 0);
      toast.success(`Reconciled ${upserted} docs · ${deleted} deleted to match Firestore`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };
  const doBackfill = async () => {
    if (!window.confirm("Push every Mongo document into Firestore? Safe to re-run. Upserts by ID.")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/firestore/backfill");
      toast.success(`Backfilled ${data.total} docs to Firestore`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally { setBackfilling(false); }
  };
  if (!s) return null;
  return (
    <Card title="Firestore. Single source of truth" right={
      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-semibold ${s.connected ? "bg-[hsl(var(--success-muted))] text-[hsl(var(--success))]" : "bg-[hsl(var(--danger-muted))] text-[hsl(var(--danger))]"}`}>
        <Database className="w-3 h-3" strokeWidth={2}/>{s.connected ? `Live · ${s.project_id}` : "Not connected"}
      </span>
    }>
      <div className="p-5 space-y-4">
        <div className="text-[12px] text-[hsl(var(--text-muted))] leading-relaxed">
          Your central Firebase project is the master record for customers, apps, and leads. Soubor One mirrors it into MongoDB so pages stay fast and customer-scoped queries work.
        </div>
        {s.writes_enabled && s.write_collections?.length > 0 && (
          <div className="rounded-lg border border-[hsl(var(--success))]/30 bg-[hsl(var(--success-muted))] p-3 flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-[hsl(var(--success))]/20 text-[hsl(var(--success))] grid place-items-center shrink-0">
              <Database className="w-4 h-4" strokeWidth={2}/>
            </div>
            <div className="min-w-0">
              <div className="text-[12px] font-semibold text-[hsl(var(--success))]">Single source of truth · {s.write_collections.length} collections live</div>
              <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">
                {s.write_collections.join(" · ")}
              </div>
              <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1">
                Every write hits Firestore first, Mongo mirrors. Open Firestore Console anytime to see the live truth.
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Customers linked" value={s.linked_customers} />
          <MiniStat label="Apps linked" value={s.linked_apps} />
          <MiniStat label="Leads linked" value={s.linked_leads} />
          <MiniStat label="Last sync" value={s.last_sync_at ? new Date(s.last_sync_at).toLocaleString() : "Never"} tight />
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] text-[hsl(var(--text-muted))]">Sync: reconcile Mongo ↔ Firestore (deletes from Firestore propagate). Backfill: push all Mongo → Firestore.</div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={doBackfill} disabled={backfilling} data-testid="firestore-backfill-btn">
              {backfilling ? <><RefreshCw className="w-4 h-4 animate-spin" strokeWidth={2}/>Backfilling…</> : <><Database className="w-4 h-4" strokeWidth={2}/>Backfill to Firestore</>}
            </Button>
            <Button onClick={doSync} disabled={syncing} data-testid="firestore-sync-btn">
              {syncing ? <><RefreshCw className="w-4 h-4 animate-spin" strokeWidth={2}/>Syncing…</> : <><RefreshCw className="w-4 h-4" strokeWidth={2}/>Sync from Firestore</>}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function GoodBarberCard() {
  const [s, setS] = useState(null);
  const load = async () => {
    try { setS((await api.get("/admin/goodbarber/status")).data); }
    catch { setS({ connected: false }); }
  };
  useEffect(() => { load(); }, []);
  if (!s) return null;
  return (
    <Card title="GoodBarber API" right={
      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-semibold ${s.connected ? "bg-[hsl(var(--success-muted))] text-[hsl(var(--success))]" : "bg-[hsl(var(--warning-muted))] text-[hsl(var(--warning))]"}`}>
        <Smartphone className="w-3 h-3" strokeWidth={2}/>{s.connected ? "Live" : "Pending credentials"}
      </span>
    }>
      <div className="p-5 space-y-3">
        <div className="text-[13px]">{s.message || "Ready once credentials are set."}</div>
        <div className="text-[12px] text-[hsl(var(--text-muted))] leading-relaxed bg-[hsl(var(--warning-muted))]/40 border border-[hsl(var(--warning))]/20 rounded-lg p-3">
          <strong>To activate:</strong> create a Public API user in GoodBarber Backoffice →
          <code className="font-mono mx-1">Publish → Public API</code> → Add a new user.
          Paste the username + password into <code>backend/.env</code> as
          <code className="font-mono mx-1">GOODBARBER_USERNAME</code> and
          <code className="font-mono mx-1">GOODBARBER_PASSWORD</code>, then restart the backend. The "Refresh stats" button on each app will come alive.
        </div>
        <div className="text-[11px] text-[hsl(var(--text-muted))]">Apps with GoodBarber IDs ready: <span className="font-mono text-[hsl(var(--text))]">{s.apps_available ?? 0}</span></div>
      </div>
    </Card>
  );
}

function MiniStat({ label, value, tight }) {
  return (
    <div className="rounded-lg bg-[hsl(var(--background))] border border-[hsl(var(--border))] px-3 py-2.5">
      <div className={`font-mono font-semibold tracking-tight ${tight ? "text-[12px]" : "text-[17px]"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] mt-0.5">{label}</div>
    </div>
  );
}

